import {
    getAdditionalUserInfo,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithCredential,
    signInWithPopup,
    updateProfile,
    UserCredential
} from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";
import { FirebaseError } from "firebase/app";
import {auth} from "../../firebaseConfig";
import {appleAuthHelpers, AppleAuthResponse} from "react-apple-signin-auth";
import {APP_CLIENT_ID} from "../constants";
import {captureMessage} from "../sentry";
import { getAttribution, getUtmSource } from "../../attribution/reader";
import { recordAttributionForUser } from "../../attribution/writer";
import {ATTRIBUTION_COOKIE_NAME, determineCookieDomain, persistUtmSourceCookie} from "../attribution.ts";

export const signInWithFirebasePopUp = async () => {
    const provider = new OAuthProvider("apple.com")
    provider.addScope("email")
    provider.addScope("name")
    const userCredential = await signInWithPopup(auth, provider);
    logLoginEvent("apple.com");
    const additionalInfo = getAdditionalUserInfo(userCredential);

    if (additionalInfo?.isNewUser) {
        await attemptAttributionPersist(userCredential);
    }
}

export const signInWithGooglePopUp = async () => {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    logLoginEvent("google.com");
    const additionalInfo = getAdditionalUserInfo(userCredential);

    if (additionalInfo?.isNewUser) {
        await attemptAttributionPersist(userCredential);
    }
}

export const signInWithAppleLibrary = async () => {
    const appleAuthResponse = await appleAuthHelpers.signIn({
        authOptions: {
            clientId: APP_CLIENT_ID,
            redirectURI: `${window.origin}/__/auth/handler`,
            scope: "email name",
            state: "state",
            usePopup: true,
        },
        onError: (error: unknown) => {
            if (!(error instanceof Error)) {
                // Wrap non-Error values in an Error object
                throw new Error(JSON.stringify(error));
            }
            throw error;
        }
    });

    if (!appleAuthResponse?.authorization) {
        throw new Error("No authorization response");
    }

    const appleCredential = new OAuthProvider("apple.com").credential({
        idToken: appleAuthResponse.authorization.id_token,
    });

    const userCredential = await signInWithCredential(auth, appleCredential);
    logLoginEvent("apple.com");
    const additionalInfo = getAdditionalUserInfo(userCredential);
    const isNewUser = additionalInfo?.isNewUser;

    if (isNewUser) {
        await createUserDisplayName(appleAuthResponse, userCredential);
        await attemptAttributionPersist(userCredential);
    }
}

// Analytics must never be able to reject a sign-in, so every auth event is
// logged through here and failures are downgraded to a Sentry warning.
const logAuthEvent = (eventName: string, method: string) => {
    try {
        logEvent(getAnalytics(), eventName, {method});
    } catch (error) {
        captureMessage(`logAuthEvent(${eventName}): ${error}`, "warning");
    }
}

/**
 * Fired when the user picks a provider, before its popup opens. Pairs with the
 * `login` event below: `login / sign_up_intent` per method is the popup
 * completion rate, which the success-only event can't show on its own.
 */
export const logSignUpIntent = (method: string) => logAuthEvent("sign_up_intent", method);

const logLoginEvent = (method: string) => logAuthEvent("login", method);

const createUserDisplayName = async (appleAuthResponse: AppleAuthResponse, userCredential: UserCredential) => {
    try {
        const {user} = appleAuthResponse;
        const firstName = user?.name?.firstName ?? "";
        const lastName = user?.name?.lastName ?? "";
        const fullName = (firstName + " " + lastName).trim();

        if (fullName) {
            await updateProfile(userCredential.user, {displayName: fullName});
        }
    } catch (error) {
        captureMessage(`createUserDisplayName: ${error}`, 'warning');
    }
}


function clearAttributionCookieEverywhere() {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return;
    }

    try {
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        const base = `${ATTRIBUTION_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;

        document.cookie = base;

        const domain = determineCookieDomain(window.location.hostname);
        if (domain !== window.location.hostname) {
            document.cookie = `${base}; Domain=${domain}`;
            const apex = domain.startsWith(".") ? domain.slice(1) : domain;
            document.cookie = `${base}; Domain=${apex}`;
        }
    } catch (err) {
        captureMessage(`clearAttributionCookieEverywhere:${err instanceof Error ? err.message : String(err)}`, "warning");
    }
}

/**
 * The signup clear consumes the click id, but utm_source must survive it:
 * the subscription-status call reads it afterwards, and utm-only visitors
 * (no click id, so no clear) keep theirs through signup too.
 */
function restoreUtmSourceCookie(utmSource: string | null) {
    if (!utmSource || typeof window === "undefined" || typeof document === "undefined") {
        return;
    }

    try {
        const domain = determineCookieDomain(window.location.hostname);
        const secure = window.location.protocol === "https:";
        persistUtmSourceCookie(document, domain, utmSource, secure);
    } catch (err) {
        captureMessage(`restoreUtmSourceCookie:${err instanceof Error ? err.message : String(err)}`, "warning");
    }
}

const attemptAttributionPersist = async (userCredential: UserCredential) => {
    try {
        const attr = getAttribution();
        if (!attr) {
            return;
        }

        // Read before the clear below wipes the cookie.
        const utmSource = getUtmSource();

        const sourceHost = typeof window !== "undefined" ? window.location.hostname : undefined;

        await recordAttributionForUser({
            uid: userCredential.user.uid,
            attr,
            sourceHost,
        });

        clearAttributionCookieEverywhere();
        restoreUtmSourceCookie(utmSource);
    } catch (error) {
        if (error instanceof FirebaseError) {
            captureMessage(`attemptAttributionPersist:${error.code}`, 'warning');
            return;
        }

        captureMessage("attemptAttributionPersist:unknown", 'warning');
    }
}
